import { z } from 'zod';

import type { ProjectStatus } from '../api-contracts.js';
import type { PlanNodeStatus } from '../project-document.js';

/**
 * Work-item contracts (M3 — PM-2023, ADR 0021/0023).
 *
 * `work_items` is the relational source of truth for the resource dimension:
 * one table, `kind` discriminator, adjacency + denormalised root. During the
 * coexistence phase the JSONB plan document remains the write format for the
 * editor/sync; every document write projects into work_items in the same
 * transaction, and the plan bridge maps rows back to `PlanNode[]`.
 *
 * Statuses are declared extensible (ADR 0023) — open strings validated by
 * Zod, never closed Postgres enums. The five known values ship as constants.
 */

export const WORK_ITEM_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'ON_HOLD',
  'COMPLETED',
  'CANCELLED',
] as const;

export type WorkItemStatus = (typeof WORK_ITEM_STATUSES)[number];

/** Extensible per ADR 0023 — validates the shape, not a closed set. */
export const WorkItemStatusSchema = z.string().min(1);

/** Tasks nest at most 5 levels beneath a Project or Delivery (ADR 0023, Data Model §3.4). */
export const MAX_WORK_ITEM_TASK_DEPTH = 5;

export const WORK_ITEM_KINDS = ['PROJECT', 'DELIVERY', 'TASK'] as const;
export type WorkItemKind = (typeof WORK_ITEM_KINDS)[number];
export const WorkItemKindSchema = z.enum(WORK_ITEM_KINDS);

// ---------------------------------------------------------------------------
// Status projections (ADR 0021/0023) — five-value work-item statuses vs the
// financial document's three-value PlanNode statuses and the projects row's
// draft|active|archived. The five→three direction is lossy by design
// (ON_HOLD and CANCELLED collapse); the write path therefore only overwrites
// a work item's status when the projection of its CURRENT status disagrees
// with the document — so distinctions made via rp routes survive plan saves.
// ---------------------------------------------------------------------------

export const workItemStatusToPlanStatus = (status: string): PlanNodeStatus => {
  switch (status) {
    case 'IN_PROGRESS':
    case 'ON_HOLD':
      return 'active';
    case 'COMPLETED':
    case 'CANCELLED':
      return 'done';
    default:
      return 'planned';
  }
};

export const planStatusToWorkItemStatus = (status: PlanNodeStatus): WorkItemStatus => {
  switch (status) {
    case 'active':
      return 'IN_PROGRESS';
    case 'done':
      return 'COMPLETED';
    default:
      return 'NOT_STARTED';
  }
};

export const workItemStatusToProjectStatus = (status: string): ProjectStatus => {
  switch (status) {
    case 'IN_PROGRESS':
    case 'ON_HOLD':
      return 'active';
    case 'COMPLETED':
    case 'CANCELLED':
      return 'archived';
    default:
      return 'draft';
  }
};

export const projectStatusToWorkItemStatus = (status: ProjectStatus): WorkItemStatus => {
  switch (status) {
    case 'active':
      return 'IN_PROGRESS';
    case 'archived':
      return 'COMPLETED';
    default:
      return 'NOT_STARTED';
  }
};

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const BudgetRollupStateSchema = z.enum(['DIRECT', 'ROLLED_UP']);
export type BudgetRollupState = z.infer<typeof BudgetRollupStateSchema>;

export const WorkItemResponseSchema = z.object({
  id: z.string().uuid(),
  kind: WorkItemKindSchema,
  /** Adjacency — null for the PROJECT row. */
  parentId: z.string().uuid().nullable(),
  /** The PROJECT work item's own id; every row of one tree shares it. */
  rootProjectId: z.string().uuid(),
  /** The JSONB financial aggregate this tree coexists with; null once decoupled. */
  financialProjectId: z.string().uuid().nullable(),
  number: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  status: WorkItemStatusSchema,
  budgetHours: z.number().nullable(),
  budgetRollupState: BudgetRollupStateSchema,
  originalBudgetHours: z.number().nullable(),
  /** Projects only. */
  probability: z.number().int().nullable(),
  /** 0 for PROJECT and DELIVERY; 1–5 for TASK beneath its project/delivery (ADR 0023). */
  depth: z.number().int(),
  startDate: z.string().nullable(),
  targetDate: z.string().nullable(),
  sortOrder: z.number().int(),
  contractId: z.string().uuid().nullable(),
  billable: z.boolean().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WorkItemResponse = z.infer<typeof WorkItemResponseSchema>;

export const RpProjectListResponseSchema = z.object({
  projects: z.array(WorkItemResponseSchema),
});
export type RpProjectListResponse = z.infer<typeof RpProjectListResponseSchema>;

export const WorkItemTreeParamsSchema = z.object({
  rootProjectId: z.string().uuid(),
});
export type WorkItemTreeParams = z.infer<typeof WorkItemTreeParamsSchema>;

export const WorkItemIdParamsSchema = z.object({
  id: z.string().uuid(),
});
export type WorkItemIdParams = z.infer<typeof WorkItemIdParamsSchema>;

// ---------------------------------------------------------------------------
// Mutation requests (M3 part B — PM-2025/2026/2027/2028)
//
// Field caps mirror the financial document's (name 200, description 4000,
// number 16 = the document's `code` cap) so every rp-written tree stays
// bridgeable into the coexisting JSONB plan (ADR 0021).
// ---------------------------------------------------------------------------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date (YYYY-MM-DD)');

const createWorkItemBaseFields = {
  name: z.string().min(1).max(200),
  number: z.string().min(1).max(16).optional(),
  description: z.string().max(4000).optional(),
  startDate: isoDate.optional(),
  targetDate: isoDate.optional(),
  budgetHours: z.number().min(0).max(1_000_000).multipleOf(0.01).optional(),
};

/**
 * Create any work-item kind (PM-2025). No `customerId` yet: there is no
 * Customer table until M12 — accepting a value we would silently drop is worse
 * than rejecting it, so the field is omitted entirely. TODO(PM-2065).
 */
export const CreateWorkItemRequestSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('PROJECT'),
    ...createWorkItemBaseFields,
    probability: z.number().int().min(0).max(100).optional(),
  }),
  z.object({
    kind: z.literal('DELIVERY'),
    /** The parent PROJECT work item's id — deliveries sit directly under the project (PM-2023). */
    projectWorkItemId: z.string().uuid(),
    ...createWorkItemBaseFields,
  }),
  z.object({
    kind: z.literal('TASK'),
    parentId: z.string().uuid(),
    ...createWorkItemBaseFields,
  }),
]);
export type CreateWorkItemRequest = z.infer<typeof CreateWorkItemRequestSchema>;

/**
 * Field update (PM-2025). Parent, kind and depth are immutable in v1;
 * `null` clears a clearable field. Date updates never cascade (DL-002).
 */
export const UpdateWorkItemRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  number: z.string().min(1).max(16).optional(),
  startDate: isoDate.nullable().optional(),
  targetDate: isoDate.nullable().optional(),
  budgetHours: z.number().min(0).max(1_000_000).multipleOf(0.01).nullable().optional(),
  /** Projects only (PROBABILITY_NOT_ALLOWED elsewhere). */
  probability: z.number().int().min(0).max(100).nullable().optional(),
});
export type UpdateWorkItemRequest = z.infer<typeof UpdateWorkItemRequestSchema>;

/** Status transition (PM-2026/2027) — the cascade is computed server-side (DL-024). */
export const UpdateWorkItemStatusRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  status: WorkItemStatusSchema,
});
export type UpdateWorkItemStatusRequest = z.infer<typeof UpdateWorkItemStatusRequestSchema>;

/**
 * A tree read enriches each item with the query-time roll-up (M4, PM-2029 /
 * ADR 0022) — numbers only, display formatting ("150 / 200h") is the UI's.
 */
export const WorkItemTreeItemSchema = WorkItemResponseSchema.extend({
  /** Σ of the direct children's operative budgets — derived, never stored. */
  childBudgetSum: z.number(),
  /** DIRECT → own budgetHours; ROLLED_UP → childBudgetSum. */
  operativeBudget: z.number().nullable(),
});
export type WorkItemTreeItem = z.infer<typeof WorkItemTreeItemSchema>;

/** Depth-first, siblings by sortOrder — render-ready without client sorting. */
export const WorkItemTreeResponseSchema = z.object({
  workItems: z.array(WorkItemTreeItemSchema),
});
export type WorkItemTreeResponse = z.infer<typeof WorkItemTreeResponseSchema>;
